// The one plan (owner, 2026-10-25: "we want to just do one price for
// everything"). Every price, name and bullet the website, the app, the
// paywalls and the admin pages print comes from here, so a change is one line.
//
// Internally the level is still the word `premium` (rb_site_level answers
// admin | premium | trial | none): that is the database's name for "has the
// plan", and nothing about the server needed to move. What people read is
// SITE_PLAN.name / .short.
//
// Decisions, RAISEDBEEF_COM_BILLING_PLAN.md §0:
//   $29.99 a month; a year at eleven months, $329.99 (owner: "that price works");
//   the 30 free days with no card are unchanged; Clara Pro stays an add-on;
//   the one Basic subscriber is grandfathered at $15 with everything.

export const SITE_PLAN = {
  // Owner, 2026-10-25: "member is good" (over All Access and Partner).
  name: "Cattle Market Central Member",
  short: "Member",
  monthly: 29.99,
  annual: 329.99,
  /** What it includes, for the plan card on the website (the order of the
   *  gated-page list in src/lib/siteAccess.ts). */
  includes: [
    "Every barn and state ticker, live",
    "The Cattle Market Central Indexes and their history",
    "Weekly Averages and Weight Breaks",
    "Sale Reports, Sale Sheet, barn archives",
    "Steer Tracker and the weight maps",
    "Fed Cattle, Packer Margin, Cattle on Feed",
    "Fat Cattle Sales, Receipts, Toppers, Diesel",
    "Clara, with 10 Clara Pro questions a day",
  ],
  /** The short list for the phone's screen B (four lines fit). The first
   *  names the one thing nobody else has (owner, 2026-10-25). */
  includesShort: ["200+ barns and every state, live as it sells", "All 19 indexes, their full history", "Weight maps, averages and trackers", "Clara answers, alerts to your phone"],
  /** The hero's claim on screen B. */
  claim: "The only place every ring prints the moment the gavel falls. Nobody else has this.",
  /** Everything a Member gets, by category, for the list under the fold on
   *  screen B. Counts are words here, not numbers the code must keep true. */
  catalogue: [
    { title: "Live feeder market", count: "10 tools", items: "Barn tickers · state tickers · Weekly Averages · Weight Breaks · 7 weight maps (4–10 wt) · Steer Tracker · Market Toppers · leaderboards · CMC Live" },
    { title: "The Cattle Market Central Indexes", count: "19 indexes", items: "Steers, heifers and calves by weight · every daily price and seven-day close, back to the first day · the methodology behind each" },
    { title: "Packer, beef & supply", count: "7 tools", items: "Fed Cattle · Packer Margin · Boxed Beef · Fat Cattle Sales · Cattle on Feed · Steer & Heifer Receipts · Diesel" },
    { title: "Reports, Clara & alerts", count: "6 tools", items: "Sale Reports · Sale Sheet · Feeder Flash · Clara with 10 Pro questions a day · market and barn alerts to your phone" },
  ],
  /** The three numbers under "Everything we publish". Barns come from the
   *  data (memberProof); these two are the catalogue's. */
  // 22 are computed; 19 are published (HIDDEN_INDEX_SLUGS holds the cow, bull and
  // 500+ composite) — the number the methodology statement prints (audit 2026-10-31).
  indexCount: 19,
  toolCount: 25,
} as const;

export type PlanInterval = "monthly" | "annual";

/** "$29.99" / "$329.99" — the website's own price string; the app shows the store's. */
export const planPrice = (interval: PlanInterval): number => (interval === "annual" ? SITE_PLAN.annual : SITE_PLAN.monthly);
export const money = (n: number): string => `$${n.toFixed(2)}`;

/** The Clara Pro add-on (owner, 2026-10-10; kept as an add-on 2026-10-25): on
 *  top of the plan, more Clara Pro questions a day. Its own Stripe
 *  subscription on the same customer, so the Customer Portal shows and
 *  cancels it on its own. */
export const CLARA_PRO_ADDON = { name: "Clara Pro", monthly: 30, annual: 330, what: "30 Clara Pro questions a day instead of 10" } as const;

/** The grandfathered Basic rate (owner, 2026-10-25: "grandfather"). One
 *  subscription, $15 a month, everything open. Used only where money is
 *  counted; it is not for sale. Step 6 of the plan removes it when that
 *  subscription ends. */
export const LEGACY_BASIC = { monthly: 15, annual: 165 } as const;
